import React from 'react';
import { X } from 'lucide-react';
import { formatInr } from './aiCallerConstants';

// Reads the shape written by ai_caller.services.cost_calculator:
//   call.usage_details = {
//     claude: {"haiku-4-5": {input, output, cache_read}, ...},
//     tts:    {"eleven_flash_v2_5": chars, ...},
//     stt:    {"sarvam_saarika": seconds},
//     twilio: {"direction": "inbound", "duration_sec": N}
//   }
// Falls back to zeros when the call predates cost tracking (older rows).

const fmtNum = (n) => (n == null ? 0 : Number(n)).toLocaleString('en-IN');

function summarizeClaude(claude) {
  if (!claude) return [];
  return Object.entries(claude).map(([model, c]) => ({
    label: `Claude ${model}`,
    detail: `${fmtNum(c.input)} in · ${fmtNum(c.output)} out · ${fmtNum(c.cache_read)} cache`,
  }));
}

function summarizeTts(tts) {
  if (!tts) return [];
  return Object.entries(tts).map(([engine, chars]) => ({
    label: engine.replace(/_/g, ' '),
    detail: `${fmtNum(chars)} chars`,
  }));
}

function summarizeStt(stt) {
  if (!stt) return [];
  return Object.entries(stt).map(([engine, secs]) => ({
    label: engine.replace(/_/g, ' '),
    detail: `${Number(secs).toFixed(1)}s`,
  }));
}

export default function CallCostBreakdown({ open, onClose, call }) {
  if (!open || !call) return null;

  const details = call.usage_details || {};
  const twilio = details.twilio || {};

  const rows = [
    {
      key: 'twilio',
      title: 'Twilio',
      subtitle: `${twilio.direction || (call.direction || 'outbound')} · ${twilio.duration_sec || call.call_duration_seconds || 0}s`,
      inr:      call.twilio_cost_inr,
    },
    {
      key: 'llm',
      title: 'Claude',
      subtitle: summarizeClaude(details.claude).map(r => `${r.label}: ${r.detail}`).join(' · ') || '—',
      inr:      call.llm_cost_inr,
    },
    {
      key: 'tts',
      title: 'ElevenLabs / Sarvam TTS',
      subtitle: summarizeTts(details.tts).map(r => `${r.label}: ${r.detail}`).join(' · ') || '—',
      inr:      call.tts_cost_inr,
    },
    {
      key: 'stt',
      title: 'Sarvam STT',
      subtitle: summarizeStt(details.stt).map(r => `${r.label}: ${r.detail}`).join(' · ') || '—',
      inr:      call.stt_cost_inr,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Call Cost Breakdown</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Locked at capture time (INR)</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map(r => (
            <div key={r.key} className="flex items-start justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white">{r.title}</div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 break-words">{r.subtitle}</div>
              </div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">{formatInr(r.inr)}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300">Total</div>
          <div className="text-base font-bold text-teal-600 dark:text-teal-400">{formatInr(call.total_cost_inr)}</div>
        </div>
      </div>
    </div>
  );
}
